import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useSelector } from "react-redux";
import { ReactComponent as SharePostIcon } from "../../../assets/svg/share-post-icon.svg";
import { ReactComponent as CopyLink } from "../../../assets/svg/copy-link.svg";
import { ReactComponent as ThumbUp } from "../../../assets/svg/thumb-up.svg";
import { ReactComponent as ThumbUpActive } from "../../../assets/svg/thumb-up-active.svg";
import { ReactComponent as MessageBubble } from "../../../assets/svg/message-bubble.svg";
import Comments from "./Comments";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import FlashMessage from "../../../components/common/FlashMessage";
import httpRequest from "../../../utils/httpRequest";
import API_URLS from "../../../utils/apiUrls";
import Avatar from "../../../components/common/AvtarImage";
import { CircularProgress } from "@material-ui/core";
import appRoutes from "../../../routes/app.routes";
import { getRemainingResendHours, openUrlInNewTab } from "../../../utils/helper";
import { connectionRequestStatus } from "../../../utils/appConstants";
import { rootReducersState } from "../../../reducers";
import searchAndConnectServices from "../../../services/searchAndConnect.services";

type ConnectInfo = {
  connect_id?: number | string | null;
  status?: number;
  reject_reason?: string | null;
  cancelled_at?: string | null;
};

type LikeUser = {
  id?: string | number;
  slug?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  profile_image?: string;
  job_title?: string | null;
};

type LikeResponseItem = {
  user: LikeUser;
  liked_at?: string;
  connect?: ConnectInfo | null;
};

interface footerProps {
  data: any;
  postLike: (data: any) => void;
  handleCopyPostLink: (data: any) => void;
  isPublicPage?: boolean;
  defaultShowComments?: boolean;
}

const FeedCommonFooter = ({
  data,
  postLike,
  handleCopyPostLink,
  isPublicPage,
  defaultShowComments = false,
}: footerProps) => {
  const [shareDropDown, setShareDropDown] = useState(false);
  const [shareMenuOpenUp, setShareMenuOpenUp] = useState(false);
  const [showComments, setShowComments] = useState(defaultShowComments);
  const [likesModalOpen, setLikesModalOpen] = useState(false);
  const [likesLoading, setLikesLoading] = useState(false);
  const [likesError, setLikesError] = useState<string>("");
  const [likedUsers, setLikedUsers] = useState<LikeResponseItem[]>([]);
  const [connectingUserId, setConnectingUserId] = useState<string | number | null>(null);
  const shareDropDownRef = useRef<HTMLDivElement | null>(null);
  const sessionReducer = useSelector(({ session }: rootReducersState) => session);
  const currentUserId = _.get(sessionReducer, "currentUser.id", "");
  const ShareDropDownStatus = () => {
    setShareDropDown((prev) => {
      const next = !prev;
      if (next) setShareMenuOpenUp(!showComments);
      return next;
    });
  };

  const postId = useMemo(() => _.get(data, "id", ""), [data]);

  const fetchLikedUsers = async () => {
    if (!postId) return;
    setLikesLoading(true);
    setLikesError("");
    try {
      const res = await httpRequest().get(API_URLS.postLikes(postId));
      const items = (_.get(res, "data.data", {}) || {}) as any;
      setLikedUsers(Array.isArray(items?.rows) ? items?.rows : []);
    } catch (e: any) {
      setLikedUsers([]);
      setLikesError(_.get(e, "response.data.message", "Failed to load likes."));
    } finally {
      setLikesLoading(false);
    }
  };

  const openLikesModal = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLikesModalOpen(true);
    await fetchLikedUsers();
  };

  const closeLikesModal = () => {
    setLikesModalOpen(false);
    setLikesError("");
  };

  const openUser = (user: LikeUser) => {
    const slug = user.slug;
    if (!slug) return;
    const url = appRoutes.candidatePublicProfile.generateFullPath(slug);
    openUrlInNewTab(url);
  };

  const getConnectStatusText = (connect?: ConnectInfo | null) => {
    const statusId = connect?.status || 0;
    return (
      connectionRequestStatus.find((row) => row.status_id === statusId)?.status_text ||
      ""
    );
  };

  const handleConnect = async (
    event: React.MouseEvent,
    row: LikeResponseItem,
    userId: string | number
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!currentUserId) {
      FlashMessage("Please log in to connect", "error");
      return;
    }

    if (String(currentUserId) === String(userId)) return;

    const pendingStatusId =
      connectionRequestStatus.find((row) => row.status_text === "pending")
        ?.status_id || 1;

    setConnectingUserId(userId);
    try {
      await searchAndConnectServices.sendInvitation({
        note: "",
        id: userId,
        ...(row.connect?.connect_id ? { connect_id: row.connect.connect_id } : {}),
      });

      setLikedUsers((prev) =>
        prev.map((item) =>
          String(item.user?.id) === String(userId)
            ? {
                ...item,
                connect: {
                  connect_id: row.connect?.connect_id || null,
                  status: pendingStatusId,
                },
              }
            : item
        )
      );
      FlashMessage("Connection request sent successfully!", "success");
    } catch (error: any) {
      FlashMessage(
        _.get(error, "response.data.message", "Failed to send connection request"),
        "error"
      );
    } finally {
      setConnectingUserId(null);
    }
  };

  const renderConnectButton = (row: LikeResponseItem) => {
    const userId = row?.user?.id;
    if (!userId || String(userId) === String(currentUserId)) return null;

    const statusText = getConnectStatusText(row.connect);
    const isLoading = String(connectingUserId) === String(userId);

    switch (statusText) {
      case "accepted":
        return (
          <Button
            type="button"
            className="likes-modal__connect-btn likes-modal__connect-btn--connected"
            disabled
          >
            Connected
          </Button>
        );
      case "pending":
        return (
          <Button
            type="button"
            className="likes-modal__connect-btn likes-modal__connect-btn--pending"
            disabled
          >
            Request Sent
          </Button>
        );
      case "rejected":
        return (
          <Button
            type="button"
            className="likes-modal__connect-btn likes-modal__connect-btn--pending"
            disabled
          >
            Rejected
          </Button>
        );
      case "cancelled_24_hours": {
        const remainingHours = getRemainingResendHours(row.connect?.cancelled_at || "");
        if (remainingHours <= 0) {
          return (
            <Button
              type="button"
              className="likes-modal__connect-btn"
              disabled={isLoading}
              onClick={(event) => handleConnect(event, row, userId)}
            >
              {isLoading ? "Sending..." : "Connect"}
            </Button>
          );
        }
        return (
          <Button
            type="button"
            className="likes-modal__connect-btn likes-modal__connect-btn--pending"
            disabled
          >
            Wait {remainingHours}h
          </Button>
        );
      }
      default:
        return (
          <Button
            type="button"
            className="likes-modal__connect-btn"
            disabled={isLoading}
            onClick={(event) => handleConnect(event, row, userId)}
          >
            {isLoading ? "Sending..." : "Connect"}
          </Button>
        );
    }
  };

  useEffect(() => {
    if (!shareDropDown) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (
        shareDropDownRef.current &&
        !shareDropDownRef.current.contains(event.target as Node)
      ) {
        setShareDropDown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [shareDropDown]);

  useEffect(() => {
    if (!shareDropDown) return;

    const button = shareDropDownRef.current;
    const menu = button?.querySelector(".share-dropDown") as HTMLElement | null;
    if (!button || !menu) return;

    const buttonRect = button.getBoundingClientRect();
    const menuHeight = menu.offsetHeight || 56;
    let clipBottom = window.innerHeight;
    let parent = button.parentElement;

    while (parent && parent !== document.body) {
      const overflowY = window.getComputedStyle(parent).overflowY;
      if (overflowY === "hidden" || overflowY === "auto" || overflowY === "scroll") {
        clipBottom = Math.min(clipBottom, parent.getBoundingClientRect().bottom);
      }
      parent = parent.parentElement;
    }

    const spaceBelow = clipBottom - buttonRect.bottom;
    setShareMenuOpenUp(spaceBelow < menuHeight + 12);
  }, [shareDropDown, showComments]);

  const totalComments = Number(_.get(data, "post_comments_count", 0)) || 0;

  return (
    <div>
      <div className="article-engagement-bar">
        <div
          className={`engagement-item cursor-pointer${_.get(data, "liked", 0) === 1 ? " liked" : ""}`}
          onClick={() => typeof postLike === "function" && postLike(data)}
        >
          {_.get(data, "liked", 0) === 1 ? (
            <ThumbUpActive className="like-icon" />
          ) : (
            <ThumbUp className="like-icon" />
          )}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); openLikesModal(e); }}
            style={{ cursor: "pointer" }}
            title="View likes"
          >
            {data.post_likes_count || 0}
            <span className="engagement-label"> Likes</span>
          </span>
        </div>

        <div className="engagement-divider" />

        <div
          className={`engagement-item cursor-pointer${showComments ? " active-comment" : ""}`}
          onClick={() => setShowComments((prev) => !prev)}
        >
          <MessageBubble className="comment-icon" />
          <span>
            {totalComments}
            <span className="engagement-label">
              {" "}
              {totalComments === 1 ? "Comment" : "Comments"}
            </span>
          </span>
        </div>

        {!isPublicPage && (
          <>
            <div className="engagement-divider" />

            <div className="engagement-item feed-share-item">
              <div
                ref={shareDropDownRef}
                className="share-button"
                onClick={() => ShareDropDownStatus()}
              >
                <SharePostIcon className="share-icon" />
                <span>
                  {data.post_shares_count || 0}
                  <span className="engagement-label"> Shares</span>
                </span>
                {shareDropDown ? (
                  <ul className={`share-dropDown${shareMenuOpenUp ? " is-open-up" : ""}`}>
                    <li
                      onClick={(e) => {
                        e.stopPropagation();
                        if (typeof handleCopyPostLink === "function") {
                          handleCopyPostLink(data);
                        }
                      }}
                    >
                      <CopyLink /> <span>Copy Link</span>
                    </li>
                  </ul>
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
      {!isPublicPage && showComments && (
        <Comments data={data} />
      )}

      <Modal
        title="Liked by"
        visible={likesModalOpen}
        size="medium"
        onClose={closeLikesModal}
        className="likes-modal"
      >
        {likesLoading ? (
          <div className="likes-modal__loading">
            <CircularProgress size={24} />
          </div>
        ) : likesError ? (
          <div className="likes-modal__error">{likesError}</div>
        ) : likedUsers.length === 0 ? (
          <div className="likes-modal__empty">No likes yet.</div>
        ) : (
          <div className="likes-modal__list">
            {likedUsers.map((row, idx) => {
              const u = row?.user || {};
              const name =
                (u.name || "").trim() ||
                `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                "User";
              const jobTitle = (u.job_title || "").trim();

              return (
                <div key={String(u.id ?? idx)} className="likes-modal__row">
                  <button
                    type="button"
                    className={`likes-modal__profile ${u.slug ? "is-clickable" : ""}`}
                    onClick={() => openUser(u)}
                    disabled={!u.slug}
                  >
                    <Avatar type="candidate" src={u.profile_image} />
                    <div className="likes-modal__meta">
                      <div className="likes-modal__name">{name}</div>
                      {jobTitle && (
                        <div className="likes-modal__subtitle">{jobTitle}</div>
                      )}
                    </div>
                  </button>
                  <div className="likes-modal__action">
                    {renderConnectButton(row)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FeedCommonFooter;
